const guide = `# Documentazione del Portale Fornitore

## Panoramica

Il Portale Fornitore consente ai fornitori di richiedere eSIM, gestire l'inventario, assegnare eSIM ai clienti e monitorare lo stato di attivazione. Questa guida illustra il flusso di lavoro completo, le API disponibili, i requisiti dell'interfaccia e il comportamento del sistema.

**Percorso base API:** \`https://r4lsolutions.com/backend/api/provider\`

> Tutti gli endpoint richiedono un token di autenticazione fornitore valido.

---

# Mappatura stato eSIM

Il backend restituisce valori di stato interni che devono essere visualizzati con etichette comprensibili per l'utente.

| Stato backend            | Etichetta visualizzata |
| ------------------------ | ---------------------- |
| \`waiting_for_assignment\` | In magazzino           |
| \`pre_service\`            | In attesa di attivazione |
| \`in_service\`             | Attivo                 |
| \`terminated\`             | Terminato              |

---

# Mappatura stato richiesta

| Stato      | Significato                                   |
| ---------- | --------------------------------------------- |
| \`pending\`  | Richiesta inviata e in attesa di approvazione |
| \`approved\` | Richiesta approvata ed eSIM aggiunte all'inventario |
| \`rejected\` | Richiesta rifiutata                           |

---

# Flusso di lavoro del fornitore

\`\`\`text
Richiedi SIM
      ↓
Approvazione richiesta
      ↓
eSIM aggiunta all'inventario
      ↓
Assegna eSIM al cliente
      ↓
Genera codice QR
      ↓
Il cliente attiva l'eSIM
      ↓
Sincronizza stato
      ↓
L'eSIM diventa attiva
\`\`\`

---

# 1. Richiedere eSIM

I fornitori possono inviare richieste per nuovo inventario eSIM.

### API

\`\`\`http
POST https://r4lsolutions.com/backend/api/provider/sim-requests
\`\`\`

### Corpo della richiesta

\`\`\`json
{
  "quantity": 5,
  "note": "Need for Q3 campaign"
}
\`\`\`

### Validazione

| Campo    | Tipo   | Obbligatorio | Note                |
| -------- | ------ | -------- | ------------------- |
| quantity | Numero | Sì       | Intervallo: 1–500   |
| note     | Testo  | No       | Commenti aggiuntivi |

### Comportamento atteso

* Creare una nuova richiesta.
* Lo stato della richiesta viene impostato automaticamente su \`pending\`.
* La richiesta appena creata deve apparire nell'elenco delle richieste.

---

# 2. Visualizzare le mie richieste SIM

I fornitori possono monitorare le richieste inviate e controllare lo stato di approvazione.

### API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/sim-requests
\`\`\`

### Esempio

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/sim-requests?page=1&pageSize=20&status=pending
\`\`\`

### Filtri disponibili

| Filtro    | Descrizione                 |
| --------- | --------------------------- |
| status    | pending, approved, rejected |
| search    | Cerca per ID richiesta      |
| startDate | Filtra per data di creazione |
| endDate   | Filtra per data di creazione |

### API statistiche

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/sim-requests/stats
\`\`\`

### Requisiti dell'interfaccia

Visualizzare:

* ID richiesta
* Quantità
* Note
* Data di creazione
* Badge di stato

### Badge di stato

* In attesa
* Approvato
* Rifiutato

> Una volta approvata una richiesta, le eSIM associate diventano automaticamente disponibili nella sezione Inventario.

---

# 3. Gestione inventario eSIM

L'inventario mostra tutte le eSIM assegnate al fornitore.

### API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/esims
\`\`\`

### Esempio

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/esims?assigned=false&status=pre_service&page=1&pageSize=20
\`\`\`

### Filtri disponibili

| Filtro    | Descrizione       |
| --------- | ----------------- |
| assigned  | true / false      |
| status    | Stato eSIM        |
| search    | Ricerca ICCID     |
| startDate | Filtro intervallo date |
| endDate   | Filtro intervallo date |

### API statistiche

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/esims/stats
\`\`\`

### Campi di risposta

| Campo           | Descrizione                  |
| --------------- | ---------------------------- |
| iccid           | Identificatore univoco eSIM  |
| activation_code | Codice di attivazione (stringa LPA) |
| status          | Stato attuale eSIM           |
| customer_id     | Cliente assegnato            |
| activated_at    | Timestamp di attivazione     |

### Requisiti dell'interfaccia

Visualizzare:

* ICCID
* Stato
* Cliente assegnato
* Data di attivazione
* Azioni

### Azioni

| Azione      | Disponibilità         |
| ----------- | --------------------- |
| Assign      | eSIM non assegnate    |
| Sync Status | Tutti i record dell'inventario |

### Viste inventario

**Disponibili per assegnazione**

\`\`\`text
assigned = false
\`\`\`

Mostra le eSIM pronte per essere assegnate ai clienti.

---

# 4. Assegnare eSIM a un cliente

Questo passaggio collega una eSIM disponibile a un cliente e genera le credenziali di attivazione.

### API

\`\`\`http
PATCH https://r4lsolutions.com/backend/api/provider/esims/{iccid}/assign
\`\`\`

### Corpo della richiesta

\`\`\`json
{
  "customer_id": "cust_abc123"
}
\`\`\`

### Risposta

Restituisce:

* Informazioni sulla eSIM assegnata
* Codice di attivazione (stringa LPA)

Esempio:

\`\`\`text
LPA:1$consumer.e-sim.global$TN2025...
\`\`\`

---

## Requisiti frontend dopo l'assegnazione

### Generare codice QR

Il \`activation_code\` restituito deve essere convertito in un codice QR.

### Visualizzare codice di attivazione

Fornire il codice di attivazione grezzo come opzione alternativa.

Esempio:

\`\`\`text
LPA:1$consumer.e-sim.global$TN2025...
\`\`\`

### Funzionalità di copia

Consentire al fornitore di copiare il codice di attivazione.

### Importante

Assegnare una eSIM **non la attiva**.

Lo stato rimane:

\`\`\`text
In attesa di attivazione
\`\`\`

finché il cliente non installa il profilo eSIM.

---

# 5. Attivazione cliente

Nessuna azione del fornitore è richiesta durante l'attivazione.

### Processo

1. Il fornitore condivide il codice QR.
2. Il cliente scansiona il codice QR sul proprio dispositivo.
3. L'operatore installa il profilo eSIM.
4. Il backend riceve la conferma di attivazione.
5. Lo stato cambia automaticamente:

\`\`\`text
pre_service → in_service
\`\`\`

### Requisito frontend

Non è necessaria alcuna chiamata API.

Il fornitore deve solo aggiornare o sincronizzare lo stato dell'inventario.

---

# 6. Sincronizzare stato di attivazione

Consente ai fornitori di recuperare gli ultimi aggiornamenti di attivazione dall'operatore.

### API

\`\`\`http
POST https://r4lsolutions.com/backend/api/provider/esims/sync
\`\`\`

### Risposta di esempio

\`\`\`json
{
  "total_checked": 5,
  "total_updated": 2,
  "updated": [
    {
      "iccid": "8910300000041170580",
      "old_status": "pre_service",
      "new_status": "in_service",
      "activated_at": "2026-06-17T08:00:00.000Z"
    }
  ],
  "failed": []
}
\`\`\`

### Comportamento frontend

Dopo una sincronizzazione riuscita:

1. Ricaricare i dati dell'inventario.
2. Aggiornare i badge di stato.
3. Visualizzare i timestamp di attivazione.
4. Spostare le eSIM attivate nello stato Attivo.

### Transizione di stato

\`\`\`text
In attesa di attivazione
          ↓
       Attivo
\`\`\`

---

# Gestione clienti

I fornitori possono gestire i clienti tramite il modulo clienti.

### API elenco clienti

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/customers
\`\`\`

### API statistiche clienti

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/customers/stats
\`\`\`

---

# Riferimento API completo

## Richieste SIM

| Metodo | Endpoint                           | Descrizione            |
| ------ | ---------------------------------- | ---------------------- |
| POST   | \`https://r4lsolutions.com/backend/api/provider/sim-requests\`       | Crea nuova richiesta SIM |
| GET    | \`https://r4lsolutions.com/backend/api/provider/sim-requests\`       | Elenca richieste SIM   |
| GET    | \`https://r4lsolutions.com/backend/api/provider/sim-requests/stats\` | Statistiche richieste  |

---

## Inventario eSIM

| Metodo | Endpoint                             | Descrizione             |
| ------ | ------------------------------------ | ----------------------- |
| GET    | \`https://r4lsolutions.com/backend/api/provider/esims\`                | Elenca inventario eSIM  |
| GET    | \`https://r4lsolutions.com/backend/api/provider/esims/stats\`          | Statistiche inventario  |
| PATCH  | \`https://r4lsolutions.com/backend/api/provider/esims/{iccid}/assign\` | Assegna eSIM al cliente |
| POST   | \`https://r4lsolutions.com/backend/api/provider/esims/sync\`           | Sincronizza stato attivazione |

---

## Clienti

| Metodo | Endpoint                        | Descrizione         |
| ------ | ------------------------------- | ------------------- |
| GET    | \`https://r4lsolutions.com/backend/api/provider/customers\`       | Elenco clienti      |
| GET    | \`https://r4lsolutions.com/backend/api/provider/customers/stats\` | Statistiche clienti |

---

# Ciclo di vita end-to-end

\`\`\`text
Il fornitore richiede eSIM
          ↓
Richiesta approvata
          ↓
eSIM aggiunta all'inventario
          ↓
Il fornitore assegna l'eSIM
          ↓
Codice QR generato
          ↓
Il cliente installa l'eSIM
          ↓
L'operatore conferma l'attivazione
          ↓
Il fornitore sincronizza lo stato
          ↓
L'eSIM diventa attiva
\`\`\`

Questo flusso di lavoro consente ai fornitori di gestire efficacemente l'inventario, distribuire eSIM ai clienti e monitorare l'avanzamento dell'attivazione da un unico portale.
`;

export default guide;
